/**
 * SuperAdminBottomNav — mobile bottom nav for Super Admin surfaces.
 *
 * Locked 2026-06-03 per owner directive (no back path inside SA pages).
 * Restyled 2026-09 (Super Admin consolidation): matches the RMD mobile
 * design system (white bar, slate border, blue active state) and gains
 * an Admin hub button so SA pages route back into the Provider-Portal
 * admin module instead of a separate shell.
 *
 *   • Mounts ONLY on `/super-admin/*` routes (skips `/super-admin/login`).
 *   • Mobile-only (`sm:hidden`).
 *   • Home → `/` (Gateway) · Admin → `/admin` hub · Dashboard → SA Command Center.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, LayoutDashboard, ShieldCheck } from 'lucide-react';

const SuperAdminBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const inSuperAdmin = location.pathname.startsWith('/super-admin');
  const onSALogin = location.pathname.startsWith('/super-admin/login');
  if (!inSuperAdmin || onSALogin) return null;

  const onDashboard = location.pathname === '/super-admin/dashboard';

  return (
    <nav
      className="sm:hidden fixed left-0 right-0 bottom-0 z-[5000] bg-white/95 backdrop-blur-md border-t border-slate-200"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
        boxShadow: '0 -2px 10px rgba(15,23,42,0.08)',
      }}
      data-testid="super-admin-bottom-nav"
      aria-label="Super Admin navigation"
    >
      <div className="flex items-stretch h-14">
        <NavButton
          icon={<Home className="w-5 h-5" />}
          label="Home"
          onClick={() => navigate('/')}
          active={false}
          testId="sa-nav-home"
        />
        <div className="w-px bg-slate-200 my-2" aria-hidden />
        <NavButton
          icon={<ShieldCheck className="w-5 h-5" />}
          label="Admin"
          onClick={() => navigate('/admin')}
          active={false}
          testId="sa-nav-admin"
        />
        <div className="w-px bg-slate-200 my-2" aria-hidden />
        <NavButton
          icon={<LayoutDashboard className="w-5 h-5" />}
          label="Dashboard"
          onClick={() => navigate('/super-admin/dashboard')}
          active={onDashboard}
          testId="sa-nav-dashboard"
        />
      </div>
    </nav>
  );
};

const NavButton = ({ icon, label, onClick, active, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-current={active ? 'page' : undefined}
    className={[
      'flex-1 flex flex-col items-center justify-center gap-0.5',
      'transition-colors active:scale-[0.96] touch-manipulation',
      active
        ? 'text-blue-600'
        : 'text-slate-500 hover:text-slate-900',
    ].join(' ')}
  >
    {icon}
    <span className="text-[11px] font-medium tracking-wide">{label}</span>
  </button>
);

export default SuperAdminBottomNav;
